import moment from "moment";
import cx from "classnames";
import { ReportDataProps } from "@components/dynamic/ReportPage";
import { SettlementDataType } from "@/api/SettlementApi";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { sortProductsByOrder } from "@utils/EDocument";
import { toNumberFormat } from "@utils/Utils";
import { formatCant } from "@utils/Cant";
import { CURRENCIES_CODES } from "@utils/Constants";

export type ClientSettlementsReportRowStyles = {
  anulada?: string;
  productDetail?: string;
};

export const buildSettlementHeaderRow = (
  settlement: SettlementDataType,
  rowStyles: ClientSettlementsReportRowStyles = {}
): ReportDataProps => {
  const anulada = !!settlement.anulada;
  const date = moment(settlement.fecha, "YYYY-MM-DD");
  const formatDate = date.format("DD-MM-YYYY");
  const currencyId = Number(settlement.currency?.currency_id || 1);

  const values: string[] = [
    `L-${settlement.numero}`,
    formatDate,
    `${settlement.client?.nombre || settlement.nombre_factura || ""}`,
    anulada ? "Si" : "No",
    `${toNumberFormat(Number(settlement.subtotal || 0))}`,
    `${toNumberFormat(Number(settlement.descuento || 0))}`,
    `${toNumberFormat(Number(settlement.impuestos || 0))}`,
    `${toNumberFormat(Number(settlement.total || 0))}`,
    CURRENCIES_CODES[currencyId],
    `${settlement.exchange_rate ?? ""}`,
    `${settlement.reference_number || ""}`,
  ];

  return {
    sourceData: settlement,
    excludeFromTotal: anulada,
    className: cx({
      [rowStyles.anulada || ""]: anulada,
    }),
    values,
  };
};

export const buildSettlementProductRow = (
  settlement: SettlementDataType,
  product: EDocumentProductDataType,
  rowStyles: ClientSettlementsReportRowStyles = {}
): ReportDataProps => {
  const anulada = !!settlement.anulada;
  const codigo = product.codigo || product.product?.codigo || "";
  const description = product.description || product.product?.descripcion_fact || "";
  const cant = Number(product.cant || 0);
  const price = Number(product.price || 0);

  const values: string[] = [
    `  ${codigo}`,
    "",
    `${description} (${formatCant(cant)} x ${toNumberFormat(price)})`,
    "",
    `${toNumberFormat(Number(product.subtotal || 0))}`,
    `${toNumberFormat(Number(product.descuento || 0))}`,
    `${toNumberFormat(Number(product.impuestos || 0))}`,
    `${toNumberFormat(Number(product.total ?? (Number(product.subtotal || 0) - Number(product.descuento || 0) + Number(product.impuestos || 0))))}`,
    "",
    "",
    "",
  ];

  return {
    sourceData: product,
    excludeFromTotal: true,
    className: cx({
      [rowStyles.productDetail || ""]: true,
      [rowStyles.anulada || ""]: anulada,
    }),
    values,
  };
};

export const sortSettlementsByFecha = (settlements: SettlementDataType[]): SettlementDataType[] => {
  return [...settlements].sort((a, b) => {
    const dateA = a.fecha ? moment(a.fecha, "YYYY-MM-DD").valueOf() : 0;
    const dateB = b.fecha ? moment(b.fecha, "YYYY-MM-DD").valueOf() : 0;
    return dateA - dateB;
  });
};

export const buildClientSettlementsReportRows = (
  settlements: SettlementDataType[],
  rowStyles: ClientSettlementsReportRowStyles = {}
): ReportDataProps[] => {
  const sorted = sortSettlementsByFecha(settlements);

  return sorted.flatMap((settlement) => {
    const headerRow = buildSettlementHeaderRow(settlement, rowStyles);
    const products = sortProductsByOrder(
      (settlement.settlementProducts || []) as EDocumentProductDataType[]
    );
    const productRows = products.map((p) =>
      buildSettlementProductRow(settlement, p, rowStyles)
    );
    return [headerRow, ...productRows];
  });
};
